import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const sliderImages = [
  {
    url: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
    title: 'Smart Printing, Smarter Savings.',
    subtitle: 'Manage all your agency leads, machine inquiries, and customer follow-ups in one powerful platform.'
  },
  {
    url: 'https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
    title: 'Streamline Your Workflow',
    subtitle: 'Track your pipeline seamlessly from initial contact to successful machine installation.'
  },
  {
    url: 'https://images.unsplash.com/photo-1543286386-2e659306cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
    title: 'Data-Driven Insights',
    subtitle: 'Monitor performance, analyze sales trends, and grow your agency faster than ever.'
  }
];

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!email || !password) {
      return setErrorMsg('Please enter both email and password');
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email: email.trim(), password });
      login(response.data);
      toast.success(`Welcome back, ${response.data.name}!`);
      navigate('/dashboard');
    } catch (error) {
      setErrorMsg(error.response?.data?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .login-wrapper {
          display: flex;
          min-height: 100vh;
          background-color: #ffffff;
          flex-direction: column;
        }
        .login-left {
          display: none;
        }
        .login-right {
          width: 100%;
          flex: 1;
          padding: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #ffffff;
        }
        .form-container {
          width: 100%;
          max-width: 520px;
          margin: 0 auto;
        }
        .password-header {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.6rem;
        }
        @media (min-width: 1024px) {
          .login-wrapper {
            flex-direction: row;
            background-color: #f1f5f9;
          }
          .login-left {
            display: block;
            width: 50%;
            position: relative;
            overflow: hidden;
          }
          .login-right {
            width: 50%;
            padding: 4rem;
          }
        }
      `}</style>
      <div className="login-wrapper">
        {/* Left Side - Image Slider */}
        <div className="login-left">
          {sliderImages.map((slide, index) => (
            <div
              key={index}
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url("${slide.url}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: currentSlide === index ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
                zIndex: currentSlide === index ? 1 : 0
              }}
            >
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', background: 'linear-gradient(to right, rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.4))' }}></div>
              <div style={{ position: 'absolute', bottom: '15%', left: '10%', right: '10%', transform: currentSlide === index ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease-out 0.2s', opacity: currentSlide === index ? 1 : 0 }}>
                <h1 style={{ fontSize: '3.5rem', fontWeight: '800', lineHeight: 1.1, marginBottom: '1.25rem', color: '#ffffff', textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                  {slide.title}
                </h1>
                <p style={{ fontSize: '1.25rem', opacity: 0.9, maxWidth: '500px', color: '#e2e8f0', lineHeight: 1.6, textShadow: '0 1px 5px rgba(0,0,0,0.3)' }}>
                  {slide.subtitle}
                </p>
              </div>
            </div>
          ))}
          {/* Slider Indicators */}
          <div style={{ position: 'absolute', bottom: '5%', left: '10%', display: 'flex', gap: '0.5rem', zIndex: 10 }}>
            {sliderImages.map((_, index) => (
              <div
                key={index}
                onClick={() => setCurrentSlide(index)}
                style={{
                  height: '4px',
                  width: currentSlide === index ? '32px' : '16px',
                  backgroundColor: currentSlide === index ? '#ffffff' : 'rgba(255,255,255,0.4)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-right">
          <div className="form-container">
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '2rem' }}>
              <img src="/logo.png" alt="Abhay Agencies" style={{ height: '60px', width: 'auto', objectFit: 'contain' }} />
            </div>
            <div style={{ textAlign: 'left', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.5rem', letterSpacing: '-0.5px' }}>Welcome Back</h2>
              <p style={{ color: '#64748b', fontSize: '1.1rem' }}>Sign in to your CRM to continue.</p>
            </div>

            {errorMsg && (
              <div style={{ backgroundColor: '#fef2f2', color: '#991b1b', padding: '1rem', borderRadius: '12px', marginBottom: '1.75rem', fontSize: '1rem', border: '1px solid #f87171', textAlign: 'left', fontWeight: '500' }}>
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.6rem', fontWeight: '600', fontSize: '1.05rem', color: '#334155' }}>Email Address</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                    <Mail size={22} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    style={{ width: '100%', padding: '1rem 1rem 1rem 3.5rem', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '1.05rem', color: '#0f172a', transition: 'all 0.2s', backgroundColor: '#f8fafc', boxSizing: 'border-box' }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; e.target.style.backgroundColor = '#ffffff'; e.target.style.boxShadow = '0 0 0 4px var(--color-primary-light)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.backgroundColor = '#f8fafc'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.6rem', fontWeight: '600', fontSize: '1.05rem', color: '#334155' }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                    <Lock size={22} />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    style={{ width: '100%', padding: '1rem 3.5rem', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '1.05rem', color: '#0f172a', transition: 'all 0.2s', backgroundColor: '#f8fafc', boxSizing: 'border-box' }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; e.target.style.backgroundColor = '#ffffff'; e.target.style.boxShadow = '0 0 0 4px var(--color-primary-light)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.backgroundColor = '#f8fafc'; e.target.style.boxShadow = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '1.25rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', transition: 'color 0.2s', padding: 0 }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#64748b'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#94a3b8'}
                  >
                    {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                  </button>
                </div>
                <div className="password-header mt-2">
                  <div></div>
                  <Link to="/forgot-password" style={{ fontSize: '1rem', color: 'var(--color-primary)', textDecoration: 'none', fontWeight: '600' }}>Forgot Password?</Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.6rem',
                  padding: '1.15rem',
                  borderRadius: '12px',
                  width: '100%',
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '1.15rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.9 : 1,
                  marginTop: '1.5rem',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 14px 0 rgba(32, 154, 146, 0.3)',
                  boxSizing: 'border-box'
                }}
                onMouseOver={(e) => { if (!loading) { e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px 0 rgba(32, 154, 146, 0.4)'; } }}
                onMouseOut={(e) => { if (!loading) { e.currentTarget.style.backgroundColor = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(32, 154, 146, 0.3)'; } }}
              >
                {loading ? <Loader2 size={22} className="spinner-icon" /> : <LogIn size={22} />}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
